import type { ApiError } from '@shared/types'

const BASE_URL = '/api'

/** Fired on any 401 so the auth store can drop a session the server no longer honours. */
export const UNAUTHORIZED_EVENT = 'argus:unauthorized'

/**
 * Every non-2xx response becomes one of these, so callers only ever catch a
 * single error type. `status` is 0 when the request never reached the server.
 */
export class ApiClientError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, string[]>

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export function isApiError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError
}

/** Safe display text for any thrown value — for toasts and store `error` fields. */
export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error) return error
  return 'Something went wrong.'
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

const STATUS_CODES: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  429: 'RATE_LIMITED'
}

function statusMessage(status: number, statusText: string): string {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return 'You do not have access to that.'
  if (status === 404) return 'Not found.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500) return 'The server ran into a problem. Please try again.'
  return statusText || `Request failed (${status}).`
}

async function toApiClientError(response: Response): Promise<ApiClientError> {
  let code = STATUS_CODES[response.status] ?? 'INTERNAL'
  let message = statusMessage(response.status, response.statusText)
  let details: Record<string, string[]> | undefined

  const raw = await response.text().catch(() => '')
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<ApiError>
      const body = parsed.error
      if (body && typeof body === 'object') {
        if (typeof body.code === 'string' && body.code) code = body.code
        if (typeof body.message === 'string' && body.message) message = body.message
        if (body.details && typeof body.details === 'object') details = body.details
      }
    } catch {
      // Proxies and static handlers answer with HTML; the status-derived message
      // is more useful to a user than a slice of that markup.
    }
  }

  return new ApiClientError(response.status, code, message, details)
}

async function parseSuccess(response: Response): Promise<unknown> {
  // 204/205 carry no body, and neither does a bodied route that chose to answer
  // empty — `response.json()` would throw on both.
  if (response.status === 204 || response.status === 205) return undefined

  const raw = await response.text()
  if (!raw) return undefined

  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  const init: RequestInit = { method, credentials: 'same-origin', headers }

  if (method !== 'GET' && body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}`, init)
  } catch {
    // fetch only rejects for transport failures. Normalise it so no caller has
    // to handle two error shapes.
    throw new ApiClientError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection.')
  }

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT))
  }

  if (!response.ok) throw await toApiClientError(response)

  return (await parseSuccess(response)) as T
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown): Promise<T> => request<T>('PATCH', path, body),
  /** `delete` is reserved, so the DELETE verb is spelled `del`. */
  del: <T>(path: string, body?: unknown): Promise<T> => request<T>('DELETE', path, body)
}
